import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../components';
import { BankNames } from './CustomerBanks';
import type { BankSummary } from '../types';

export interface ProjectParticipant {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  status?: string;
  serviceType?: string;
  providerType?: string;
  type?: string;
  lawyerCompanyName?: string;
  inspectorCompanyName?: string;
}

export interface ProjectParticipantsData {
  customer?: { name?: string; email?: string; phone?: string; customerNumber?: number | string; banks?: BankSummary[] } | null;
  company?: { companyName?: string; companyNumber?: number | string; email?: string; phone?: string } | null;
  providers?: ProjectParticipant[];
}

export interface ProjectParticipantsTabProps {
  /**
   * Participants for the project. The caller is responsible for fetching a
   * role-appropriate, permission-scoped payload — this component only renders
   * what it is given and never decides what a role may see.
   */
  participants: ProjectParticipantsData | null;
  /** Shown instead of the empty Beställare card, e.g. "Lägg till beställare" (ClickUp 869fmxjgt). */
  customerSlot?: React.ReactNode;
}

/**
 * Shared participants tab: one Privatperson card and one Företag card (there is
 * always at most one of each), plus separate Jurister and Besiktningsmän cards
 * (a project can have several of each) with role/status badges and per-role
 * empty states. Used by the company view and reusable by the lawyer/inspector
 * views for identical visual language.
 */
export function ProjectParticipantsTab({ participants, customerSlot }: ProjectParticipantsTabProps) {
  const { t } = useTranslation();

  const providerList: ProjectParticipant[] = participants?.providers ?? [];
  const providerTypeOf = (p: ProjectParticipant) => p.serviceType ?? p.providerType ?? p.type;
  const lawyers = providerList.filter(p => providerTypeOf(p) === 'lawyer');
  const inspectors = providerList.filter(p => providerTypeOf(p) === 'inspector');

  const renderProvider = (p: ProjectParticipant) => {
    const isLawyer = providerTypeOf(p) === 'lawyer';
    const initials = (p.name || '')
      .split(' ')
      .map((n: string) => n[0])
      .filter(Boolean)
      .join('')
      .substring(0, 2)
      .toUpperCase();
    const accepted = p.status === 'accepted';
    const company = p.lawyerCompanyName || p.inspectorCompanyName;
    return (
      <div key={p.id} className="border rounded p-3">
        <div className="d-flex align-items-start gap-3">
          <div
            className={`avatar-text rounded-circle ${isLawyer ? 'bg-primary' : 'bg-info'} text-white d-flex align-items-center justify-content-center flex-shrink-0`}
            style={{ width: 48, height: 48, fontSize: 16 }}
          >
            {initials}
          </div>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
              <span className="fw-bold">{p.name}</span>
              <span className={`badge ${isLawyer ? 'bg-primary' : 'bg-info'}`}>
                {isLawyer ? t('projectView.participants.lawyer', 'Jurist') : t('projectView.participants.inspector', 'Besiktningsman')}
              </span>
              <span className={`badge ${accepted ? 'bg-success' : 'bg-warning text-dark'}`}>
                {accepted ? (
                  <><i className="feather-check me-1" style={{ fontSize: 10 }}></i>{t('projectView.participants.statusAccepted', 'Inbjudan accepterad')}</>
                ) : t('projectView.participants.statusPending', 'Väntar på svar')}
              </span>
            </div>
            {company && (
              <div className="text-muted small mb-1"><i className="feather-briefcase me-1"></i>{company}</div>
            )}
            <div className="row mt-2">
              <div className="col-sm-6 mb-2 mb-sm-0">
                <small className="text-muted d-block">{t('customers.columns.email')}</small>
                <a href={`mailto:${p.email}`} className="small text-primary text-break">{p.email}</a>
              </div>
              {p.phone && (
                <div className="col-sm-6">
                  <small className="text-muted d-block">{t('customers.columns.phone')}</small>
                  <span className="small">{p.phone}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const providerColumn = (title: string, icon: string, list: ProjectParticipant[], emptyText: string) => (
    <div className="col-lg-6">
      <Card className="h-100 mb-0">
        <div className="card-header">
          <h5 className="card-title mb-0"><i className={`${icon} me-2`}></i>{title}</h5>
        </div>
        <div className="card-body">
          {list.length > 0 ? (
            <div className="d-flex flex-column gap-3">{list.map(renderProvider)}</div>
          ) : (
            <div className="text-center py-4 text-muted">
              <i className="feather-user-plus fs-1 d-block mb-3"></i>
              <p className="mb-0">{emptyText}</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );

  return (
    <div className="tab-pane fade show active">
      <div className="row g-4">
        {/* Privatperson / Kund (alltid högst en) */}
        <div className="col-lg-6">
          <Card className="h-100 mb-0">
            <div className="card-header">
              <h5 className="card-title mb-0"><i className="feather-user me-2"></i>{t('projectView.participants.customer', 'Beställare')}</h5>
            </div>
            <div className="card-body">
              {participants?.customer ? (
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-muted small">{t('customers.columns.name')}</label>
                    <div className="fw-semibold">{participants.customer.name}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-muted small">{t('customers.columns.email')}</label>
                    <div><a href={`mailto:${participants.customer.email}`} className="text-primary text-break">{participants.customer.email}</a></div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-muted small">{t('customers.columns.phone')}</label>
                    <div>{participants.customer.phone || '—'}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-muted small">{t('customers.columns.customerNumber', 'Beställarnummer')}</label>
                    <div>#{participants.customer.customerNumber}</div>
                  </div>
                  {participants.customer.banks && (
                    <div className="col-12 mb-3">
                      {/* The Beställare's banks, read-only here (ClickUp 869eu6fpm). */}
                      <label className="form-label text-muted small">{t('banks.customerTitle')}</label>
                      <div><BankNames banks={participants.customer.banks} /></div>
                    </div>
                  )}
                </div>
              ) : customerSlot ?? (
                <p className="text-muted mb-0">{t('common.noData', 'Ingen information')}</p>
              )}
            </div>
          </Card>
        </div>

        {/* Företag (alltid högst ett) */}
        <div className="col-lg-6">
          <Card className="h-100 mb-0">
            <div className="card-header">
              <h5 className="card-title mb-0"><i className="feather-briefcase me-2"></i>{t('projectView.participants.company', 'Hantverkare')}</h5>
            </div>
            <div className="card-body">
              {participants?.company ? (
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-muted small">{t('adminCompanyList.columns.companyName')}</label>
                    <div className="fw-semibold">{participants.company.companyName}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-muted small">{t('adminCompanyList.columns.companyNumber')}</label>
                    <div>#{participants.company.companyNumber}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-muted small">{t('adminCompanyList.columns.email', 'Kontakt e-post')}</label>
                    <div><a href={`mailto:${participants.company.email}`} className="text-primary text-break">{participants.company.email}</a></div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-muted small">{t('adminCompanyList.columns.phone', 'Telefon')}</label>
                    <div>{participants.company.phone || '—'}</div>
                  </div>
                </div>
              ) : (
                <p className="text-muted mb-0">{t('common.noData', 'Ingen information')}</p>
              )}
            </div>
          </Card>
        </div>

        {/* Jurister (flera möjliga) */}
        {providerColumn(
          t('projectView.participants.lawyers', 'Jurister'),
          'feather-shield',
          lawyers,
          t('projectView.participants.noLawyers', 'Inga jurister har bjudits in ännu')
        )}

        {/* Besiktningsmän (flera möjliga) */}
        {providerColumn(
          t('projectView.participants.inspectors', 'Besiktningsmän'),
          'feather-clipboard',
          inspectors,
          t('projectView.participants.noInspectors', 'Inga besiktningsmän har bjudits in ännu')
        )}
      </div>
    </div>
  );
}
